import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { Direction } from '../../models/direction.enum';
import { DisplayStatus } from '../../models/displaystatus.enum';
import { Source } from '../../models/source.enum';
import { Status } from '../../models/status.enum';
import { TrackAuthority } from '../../models/trackauthority';
import { IUserConfirmation, IUserConfirmationLabel } from '../../models/userconfirmation';
import { AffirmationService } from '../../services/affirmation.service';
import { ChangeRequestService } from '../../services/changerequest.service';
import { EventBusService, EventType, ATMEventData } from '../../services/eventbus.service';
import { HttpResponse } from '../../models/http';
import { ChangeRequest } from '../../models/changerequest';
import { LineItem } from '../../models/lineitem';
import { UtilService } from '../../services/util.service';
import { ChangeRequestType } from '../../models/changerequesttype.enum';
import { DataShareService } from '../../services/data-share.service';

@Component({
  selector: 'changerequest-affirmations',
  templateUrl: './changerequest-create-affirmations.component.html',
  styleUrls: ['./changerequest-create-affirmations.component.scss']
})
export class ChangeRequestAffirmationsComponent implements OnChanges {
  Direction = Direction;
  Status = Status;
  Source = Source;
  userConfirmationList: IUserConfirmation[];
  changeRequestConfirmation: IUserConfirmation;
  allAccepted = false;
  display = false;
  showPop = false;
  blockDialog = true;
  title = '';
  loading = false;

  @Input() authority: TrackAuthority = null;
  @Input() changeRequest: ChangeRequest;
  @Input() confirmation;
  @Output() onCloseDialog = new EventEmitter();
  @Output() onSubmit = new EventEmitter();
  @Input() lineItems: Array<LineItem>;

  constructor(
    private affirmationService: AffirmationService,
    private changeRequestService: ChangeRequestService,
    private eventBus: EventBusService,
    private dataShareService: DataShareService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.showPop = this.confirmation;
    this.display = this.authority != null;
    this.blockDialog = true;
    this.userConfirmationList = new Array<IUserConfirmation>();
    this.allAccepted = false;
    if (changes.authority && this.display) {
      this.affirmationService.getAffirmations(this.authority, Status.CHANGE_REQUEST).then(response => {
        this.userConfirmationList = response;
        this.filterConfirmationMessages();
        this.addChangeRequestConfirmations();
        this.blockDialog = false;
      });
    }
    this.title = 'Modify: Authority';
    if (
      this.authority !== null &&
      this.authority.ec1Number !== undefined &&
      this.authority.ec1Number !== null &&
      (this.authority.displayStatus === DisplayStatus.ACTIVE || this.authority.displayStatus === DisplayStatus.RELEASED)
    ) {
      this.title = this.title + ' #' + this.authority.ec1Number;
    }
  }

  toggleUserConfirmationLabelStatus(userConfirmation: IUserConfirmation, userConfirmationLabel: IUserConfirmationLabel): void {
    let requestedStatus: boolean = !userConfirmationLabel.accepted;
    if (userConfirmation.confirmType === 'ME') {
      for (let userConfirmationLabelItem of userConfirmation.userConfirmationLabelList) {
        userConfirmationLabelItem.accepted = false;
      }
    }
    userConfirmationLabel.accepted = requestedStatus;
    this.updateAllReviewed();
  }

  setBlock(visible: boolean): void {
    this.blockDialog = visible;
  }
  submitChangeRequest(): void {
    this.changeRequest.confirmationMessageList = this.userConfirmationList;
    this.loading = true;
    this.setBlock(true);
    this.changeRequestService
      .postChangeRequest(this.changeRequest, this.authority)
      .then(
        success => {
          this.dataShareService.setModifyOrReportByChangeRequestStatus(this.authority, Status.CHANGE_REQUEST.toString());
          this.eventBus.fireEvent(EventType.AUTHORITY_UPDATED_EVENT, new ATMEventData(this.authority, () => {}));
          this.closeAffirmations();
          this.loading = false;
          this.setBlock(false);
          this.onSubmit.emit();
        },
        (error: HttpResponse) => {
          this.closeAffirmations();
          this.loading = false;
          this.setBlock(false);
          this.onSubmit.emit();
        }
      )
      .catch(response => {
        this.closeAffirmations();
        this.loading = false;
        this.setBlock(false);
        this.onSubmit.emit();
      });
  }

  private updateAllReviewed(): void {
    this.allAccepted = true;
    for (let userConfirmation of this.userConfirmationList) {
      userConfirmation.accepted = !(userConfirmation.confirmType === 'ME');
      for (let userConfirmationLabel of userConfirmation.userConfirmationLabelList) {
        if (userConfirmation.confirmType === 'ME') {
          userConfirmation.accepted = userConfirmation.accepted || userConfirmationLabel.accepted;
        } else {
          userConfirmation.accepted = userConfirmation.accepted && userConfirmationLabel.accepted;
        }
      }

      this.allAccepted = this.allAccepted && userConfirmation.accepted;
    }
  }

  private addChangeRequestConfirmations(): void {
    if (this.changeRequest.type === ChangeRequestType.REPORTBY.toString()) {
      this.userConfirmationList.push({
        accepted: false,
        confirmType: 'A',
        title: 'Report By ',
        userConfirmationLabelList: [
          {
            accepted: false,
            confirmMessage:
              'Report By: ' +
              Direction[this.authority.lineItems[0].direction] +
              ' of ' +
              this.changeRequest.reportBy.displayName +
              ' on Track ' +
              this.changeRequest.reportBy.location.track.name
          }
        ]
      } as IUserConfirmation);
    } else if (this.changeRequest.type === ChangeRequestType.MODIFY.toString()) {
      let cancelLineItemConfirmationLabels: IUserConfirmationLabel[] = [];
      let directionChangeConfirmationLabels: IUserConfirmationLabel[] = [];
      let timeChangeConfirmationLabel: IUserConfirmationLabel[] = [];

      if (
        this.changeRequest.modification.lineItems !== undefined &&
        this.changeRequest.modification.lineItems.length > 0 &&
        this.lineItems !== undefined
      ) {
        this.lineItems.forEach(item => {
          if (item.isUserCancelled) {
            cancelLineItemConfirmationLabels.push(
              {
                accepted: false, confirmMessage: 'Cancel Line ' + item.lineNumber +
                  '; Operating From: ' + item.fromPoint.displayName + ',' +
                  item.fromPoint.location.track.name
                  + '; Operating To: '
                  + item.toPoint.displayName + ',' + item.toPoint.location.track.name
              });
          } else if (item.newDirection !== item.direction) {
            directionChangeConfirmationLabels.push(
              {
                accepted: false, confirmMessage: 'Direction changed to ' + Direction[item.newDirection] +
                  ': Line ' + item.lineNumber + '; ' + 'Operating From: ' +
                  item.fromPoint.displayName + ',' + item.fromPoint.location.track.name + '; Operating To:  '
                  + item.toPoint.displayName + ',' + item.toPoint.location.track.name
              });
          }
        });
      }

      if (this.changeRequest.modification.untilTime !== undefined) {
        const dateAffirmation = UtilService.estToLocal(this.changeRequest.modification.untilTime);

        timeChangeConfirmationLabel.push({
          accepted: false,
          confirmMessage:
            'Time Extended Until: ' +
            dateAffirmation.toLocaleString('en', {
              year: 'numeric',
              month: '2-digit',
              day: 'numeric',
              hour12: false,
              hour: '2-digit',
              minute: '2-digit'
            })
        });

        this.userConfirmationList.push({
          accepted: false,
          confirmType: 'A',
          title: 'Time Extension',
          userConfirmationLabelList: timeChangeConfirmationLabel
        } as IUserConfirmation);
      }
      if (directionChangeConfirmationLabels.length > 0) {
        this.userConfirmationList.push({
          accepted: false,
          confirmType: 'A',
          title: 'CHANGE DIRECTION',
          userConfirmationLabelList: directionChangeConfirmationLabels
        } as IUserConfirmation);
      }
      if (cancelLineItemConfirmationLabels.length > 0) {
        this.userConfirmationList.push({
          accepted: false,
          confirmType: 'A',
          title: 'CANCEL LINE',
          userConfirmationLabelList: cancelLineItemConfirmationLabels
        } as IUserConfirmation);
      }
    }
  }
  closeAffirmations() {
    this.showPop = false;
    this.onCloseDialog.emit({ value: 'false' });
  }

  private filterConfirmationMessages(): void {
    let cancelLine = false;
    let directionChange = false;

    if (this.changeRequest.type === 'MODIFY') {
      this.lineItems.forEach(item => {
        if (item.isUserCancelled) {
          cancelLine = true;
        }
        if (item.newDirection !== item.direction) {
          directionChange = true;
        }
      });

      if (cancelLine) {
        return;
      } else if (!cancelLine && !directionChange) {
        this.userConfirmationList = new Array<IUserConfirmation>();
      } else if (!cancelLine && directionChange) {
        this.userConfirmationList.shift();
        this.userConfirmationList.shift();
        this.userConfirmationList.shift();
      }
    } else {
      return;
    }
  }
}
